class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close()
    );
    this.addEventListener('click', (event) => {
      if (event.target.matches('#CartDrawer-Overlay')) this.close();
      const footerHeader = event.target.closest('.cart-drawer__footer-header');
      if (footerHeader && this.contains(footerHeader)) this.toggleSummary(footerHeader);
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.drawerReady === 'true') return;
    cartLink.dataset.drawerReady = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  toggleSummary(container) {
    const containerBody = container.parentElement.querySelector(
      '.cart-drawer__footer-body'
    );
    if (container) container.classList.toggle('active');
    if (containerBody) containerBody.classList.toggle('open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    if (!cartDrawerNote?.nextElementSibling) return;
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    const shouldOpen = options.open !== false;
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionElement || !sectionHtml) return;
      const replacement = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (replacement != null) sectionElement.innerHTML = replacement;
    });

    if (shouldOpen) requestAnimationFrame(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML ?? null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

function defineCartDrawerItems() {
  if (!window.CartItems || customElements.get('cart-drawer-items')) return;

  class CartDrawerItems extends window.CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}

defineCartDrawerItems();
document.addEventListener('cart:ready', defineCartDrawerItems, { once: true });

async function removeLegacyShippingProtection() {
  try {
    await window.queueCartMutation(async () => {
      const cartResponse = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) return;
      const cartState = await cartResponse.json();
      const protectionItem = cartState.items.find((item) =>
        item.handle === 'shipping-protection' || item.product_handle === 'shipping-protection'
      );
      if (!protectionItem) return;

      const response = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          id: protectionItem.key,
          quantity: 0,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });
      const updatedCart = await response.json();
      if (!response.ok || updatedCart.errors) throw new Error(updatedCart.errors || 'Unable to clean up cart.');
      document.querySelector('cart-drawer')?.renderContents(updatedCart, { open: false });
      if (window.location.pathname === routes.cart_url) window.location.reload();
    });
  } catch (error) {
    console.error('[cart] legacy shipping protection cleanup failed:', error);
  }
}

document.addEventListener('DOMContentLoaded', removeLegacyShippingProtection);
